import React, { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Accessibility, AlertTriangle, CircleAlert, Info, RefreshCw, Crosshair, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const SEV = {
  error: { icon: CircleAlert, cls: "text-[color:var(--danger)]", label: "Error" },
  warning: { icon: AlertTriangle, cls: "text-amber-500", label: "Warning" },
  info: { icon: Info, cls: "text-[color:var(--brand-ocean)]", label: "Info" },
};

// Item 19: Builder-side accessibility validator (WCAG 2.2 AA / Section 508 rules from backend/a11y.py).
export function A11yAuditPanel({ open, onOpenChange, dashboard, onSelectWidget, initialReport = null }) {
  const [report, setReport] = useState(initialReport);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async () => {
    if (!dashboard) return;
    setBusy(true);
    try {
      const { data } = await api.post("/a11y/audit", { widgets: dashboard.widgets || [], canvas: dashboard.canvas || {}, name: dashboard.name });
      setReport(data);
    } catch { setReport(null); } finally { setBusy(false); }
  }, [dashboard]);
  useEffect(() => { if (open) run(); }, [open, run]);
  useEffect(() => { if (initialReport) setReport(initialReport); }, [initialReport]);
  const issues = report?.issues || [];
  const scoreColor = !report ? "" : report.score >= 90 ? "text-[color:var(--success)]" : report.score >= 70 ? "text-amber-500" : "text-[color:var(--danger)]";
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-[color:var(--canvas)]" data-testid="a11y-audit-panel" aria-describedby="a11y-audit-desc">
        <SheetHeader className="px-5 py-4 border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]">
          <SheetTitle className="flex items-center gap-2 font-display"><Accessibility className="w-5 h-5 text-[color:var(--brand-ocean)]" aria-hidden="true" />Accessibility check</SheetTitle>
          <SheetDescription id="a11y-audit-desc" className="text-xs">WCAG 2.2 {report?.level || "AA"} · Section 508 — titles, contrast, colour-only cues, alt text, font sizes.</SheetDescription>
        </SheetHeader>
        <div className="px-4 pt-3 flex items-center gap-2">
          <Button data-testid="a11y-audit-rerun" size="sm" variant="outline" className="bg-[color:var(--surface)]" onClick={run} disabled={busy}>
            <RefreshCw className={cn("w-3.5 h-3.5 mr-1", busy && "animate-spin")} aria-hidden="true" />Re-check
          </Button>
          {report && (
            <span className="ml-auto text-[11px] text-[color:var(--ink-3)]" data-testid="a11y-audit-summary">
              <b data-testid="a11y-audit-score" className={cn("text-base font-display mr-1", scoreColor)}>{report.score}</b>/100 ·
              {" "}{report.counts.errors} errors · {report.counts.warnings} warnings · {report.widgets_checked} visuals
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0 overflow-auto px-4 py-3 space-y-2" role="list" aria-label="Accessibility findings">
          {report && issues.length === 0 && (
            <div data-testid="a11y-audit-clean" className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-4 text-sm text-[color:var(--ink-2)] flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[color:var(--success)]" aria-hidden="true" />No accessibility issues found. Screen-reader tables, focus outlines and skip links are provided automatically.
            </div>
          )}
          {issues.map((it, i) => {
            const S = SEV[it.severity] || SEV.info;
            return (
              <div key={i} role="listitem" data-testid={`a11y-issue-${it.rule}`} className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-3">
                <div className="flex items-start gap-2">
                  <S.icon className={cn("w-4 h-4 mt-0.5 shrink-0", S.cls)} aria-label={S.label} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-[color:var(--ink)] truncate">{it.widget_title} <span className="text-[color:var(--ink-3)] font-normal">· {it.rule}{it.wcag ? ` · WCAG ${it.wcag}` : ""}</span></div>
                    <div className="text-xs text-[color:var(--ink-2)] mt-0.5">{it.message}</div>
                    <div className="text-[11px] text-[color:var(--ink-3)] mt-0.5">Fix: {it.fix}</div>
                  </div>
                  {it.widget_id && onSelectWidget && (
                    <button data-testid={`a11y-issue-select-${i}`} onClick={() => onSelectWidget(it.widget_id)} aria-label={`Select ${it.widget_title}`} title="Select widget"
                      className="p-1 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]"><Crosshair className="w-3.5 h-3.5" aria-hidden="true" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
